<template>
  <cs-widget
    v-if="filteredNews.length > 0"
    :title="t('dashboard.news')"
    class="card-wrapper"
  >
    <div class="nouvaute">
      <el-row
        v-for="newsindex in filteredNews"
        :key="newsindex.ID"
        :gutter="12"
        class="cards"
        @click="openNews(newsindex.ID ?? 0)"
      >
        <div shadow="hover" class="card-item" v-loading="loadingImgs">
          <el-row :gutter="6" class="news-data">
            <img
              :alt="t('images.srh4u')"
              class="small_img"
              :src="
                newsindex.Image?.Url && newsindex.NewsCategoryLookupId != 6
                  ? newsindex.Image?.Url
                  : require('@/assets/images/video.png')
              "
              @load="imagesLoad"
            />
            <div style="width: 100%">
              <div class="title-link">
                <cs-link
                  :link="`${TEAMSRH4U_WEBPAGE}${newsindex.ID}`"
                  is-external-link
                  target="_blank"
                  :aria-label="
                    t('links.openLinkNewWindow', {
                      name: String(newsindex.Title),
                    })
                  "
                  @click.stop
                >
                  <b class="titles">{{ getTitle(String(newsindex.Title)) }}</b>
                </cs-link>
              </div>
              <div class="date-news">
                <p>
                  {{
                    getDate(
                      newsindex.Modified ? newsindex.Modified : '',
                      monthNames
                    )
                  }}
                </p>
              </div>
              <div class="description">
                <p>
                  {{ getDescription(String(newsindex.MailIntroduction ?? '')) }}
                </p>
              </div>
            </div>
          </el-row>
        </div>
      </el-row>
    </div>
    <template #footer>
      <div class="card-footer">
        <p class="footer-text">{{ t('footerNews') }}</p>
        <a
          v-for="social in socialLinks"
          :key="social.id"
          :href="social.link"
          target="_blank"
          rel="noopener noreferrer"
          :aria-label="
            t(
              'socialMedia.' +
                (social.title === 'X' ? 'X' : social.title.toLowerCase())
            )
          "
          ><img
            v-if="social.imageBase64"
            :src="social.imageBase64"
            alt=""
            aria-hidden="true"
        /></a>
      </div>
    </template>
  </cs-widget>
</template>

<script setup lang="ts">
import { getDate, getDescription, getTitle } from '@/utils/ui';
import { UseStoreNews } from '@/store/newsStore';
import { computed, ref, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { TEAMSRH4U_WEBPAGE } from '@/constants/liens';
import socialLinkService from '@/services/socialLinkService';
import type { SocialLinkResponse } from '@/types/api';

const openNews = (id: number) => {
  window.open(`${TEAMSRH4U_WEBPAGE}${id}`, '_blank');
};

const newsStore = await UseStoreNews();
const currentCat = ref(6);
const currentNews = ref('');
const { t, locale } = useI18n();
const loading = ref(false);
const loadingImgs = ref(true);
const loadingIndex = ref(1);
const socialLinks = ref<SocialLinkResponse[]>([]);
const filteredNews = computed(() => newsStore.news);

const loadSocialLinks = async () => {
  try {
    const lang = locale.value?.slice(0, 2) || 'fr';
    socialLinks.value = await socialLinkService.getSocialLinks(lang);
  } catch {
    socialLinks.value = [];
  }
};

onMounted(loadSocialLinks);

const monthNames = [
  t('months.january'),
  t('months.february'),
  t('months.march'),
  t('months.april'),
  t('months.may'),
  t('months.june'),
  t('months.july'),
  t('months.august'),
  t('months.september'),
  t('months.october'),
  t('months.november'),
  t('months.december'),
];

const imagesLoad = () => {
  loadingIndex.value++;
  if (loadingIndex.value % newsStore.news.length == 0) {
    loadingImgs.value = false;
  }
};
</script>

<style scoped>
.news-data {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: flex-start;
  gap: 0.5rem;
}
el-row {
  border-color: white;
}
.title {
  padding: 1rem 1rem 1rem 0px;
}
.small_img {
  width: 6.5rem;
  height: 6.25rem;
  object-fit: cover;
  border-radius: 0.5rem;
}
.titles {
  font-size: 0.75rem;
  line-height: 1.25rem;
  font-weight: 700;
  color: #333;
  margin: 4px 0px 0px 0px;
}

.title-link {
  display: flex;
  align-items: center;
}
.cs-link {
  gap: 0.25rem;
}
.cs-link:hover:not(.cs-link-disabled) {
  background: none;
}
.cards {
  margin-bottom: 0.5rem;
  cursor: pointer;
  transition: background 0.2s;
}
.cards:hover {
  background: #f5f5f5;
}
.cards:hover .titles {
  color: #0167ff;
}
.nouvaute {
  padding: 1rem 1rem 0px 1rem;
  flex: 1; /* This fills all available vertical space */
}
@media screen and (min-width: 2100px) {
  .titles {
    font-size: 1rem !important;
  }
  .description {
    font-size: 0.825rem !important;
  }
  .card-item {
    height: 7.4rem !important;
  }
  .small_img {
    width: 7.5rem !important;
    height: 7.25rem !important;
  }
  .card-wrapper {
    margin-bottom: 1rem !important;
  }
}
.description {
  font-size: 0.75rem;
  line-height: 1rem;
  font-weight: 400;
  color: #595959;
  margin-top: 0.25rem;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
}
.card-item {
  width: 100%;
  height: auto;
}
p {
  margin: 0;
}
.card-footer {
  width: 100%;
  padding: 0.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.75rem;
}
.card-wrapper {
  height: 34rem;
}

@media screen and (min-width: 1700px) and (max-width: 1900px) {
  .card-wrapper {
    height: 37rem;
  }
}

@media screen and (min-width: 1920px) and (max-width: 2100px) {
  .card-wrapper {
    height: 40rem;
  }
}
@media screen and (min-width: 2101px) {
  .card-wrapper {
    height: 46.5rem;
  }
}

.card-footer img,
.global-footer img {
  width: 1.25rem;
  height: 1.25rem;
  cursor: pointer;
  transition: transform 0.2s;
}
.footer-text {
  font-style: italic;
  font-weight: 500;
  font-size: 0.75rem;
  color: #808080;
  margin: 0;
}
.date-news {
  font-family: Nunito, sans-serif;
  font-weight: 400;
  line-height: 0.875rem;
  font-size: 12px;
  color: #595959;
  margin: 0.25rem 0px;
}

.card-footer img:hover,
.global-footer img:hover {
  transform: scale(1.1);
}
.card-footer img {
  width: 1rem;
  height: 1rem;
}
@media screen and (max-width: 768px) {
  .news-data {
    flex-direction: column;
    align-items: flex-start;
  }

  .small_img {
    width: 100%;
    height: auto;
  }

  .card-item {
    height: auto;
    padding-bottom: 8px;
  }

  .description {
    -webkit-line-clamp: 3;
    font-size: 13px;
    line-height: 18px;
  }

  .titles {
    font-size: 16px;
    line-height: 22px;
  }

  .title {
    font-size: 18px;
    padding: 12px 8px 8px 0;
  }

  .card-footer {
    flex-direction: column;
    gap: 8px;
  }

  .footer-text {
    font-size: 13px;
    text-align: center;
  }
}
</style>
